// Export as PNG… and Export as PDF… (design-docs/Maps — design.md ▸ Export): the whole map drawn as the canvas draws it —
// the same `MapSketch` marks, regions smudged, the charcoal rubbed through a grain, linked notes as cards and chips —
// without the selection, the knob or handles, on Paper's colours whatever the appearance, with the notes' own faces
// (Newsreader, Caveat). The drawing is worked out apart from the browser (`exportScene`: notes laid out by a measuring
// function, as the canvas's CSS lays them out, at their full height and with all their words) and then painted on a 2D
// canvas (`paintScene`: the marks as `Path2D`s of the same path data, the text with the page's loaded fonts), which
// avoids an SVG image's isolation from the page's fonts and stays inside the policy (no style attributes, no data:
// images). A PNG is that canvas at 2×; a PDF is one page the map's size holding the same picture as a JPEG.
import {
  entityCSSColor, MapArrange, MapLayout, MapSketch, placedStroke, type InkColor, type MapContent, type MapFrame, type MapNote, type MapOutline, type MapPoint,
  type Project, type SketchMark, type SketchStyle,
} from '@writing-suite/core';
import { linkShown, markPath, midPoint, strokeMarkPoints } from './mapDraw.ts';
import { cardInfo, chapterLabels, linkLives, shownPlaces, stackLine } from './mapProject.ts';

/** Paper's colours (app.css `:root`, maps.css `--map-ink`, filter.css's inks): what an export is drawn in. */
export const PAPER = {
  paper: '#fffdf9',
  ink: '#2f2a26',
  muted: '#6e6157',
  faint: '#9a8e83',
  inks: {
    red: '#b23a2e', orange: '#b85e17', yellow: '#8f7209', green: '#3e7d3a', blue: '#2f5e9e', pink: '#ae4577', purple: '#6e4a9e', grey: '#7a736b',
  } as Record<InkColor, string>,
};

const SERIF = "'Newsreader', 'Iowan Old Style', Georgia, serif";
const HAND = "'Caveat', 'Segoe Print', cursive";
/** Room round the map in an export. */
export const EXPORT_MARGIN = 40;

const inkOf = (ink: InkColor | null | undefined) => (ink ? PAPER.inks[ink] : PAPER.ink);

/** What the canvas measures text with: its width, and the font's ascent and descent. */
export type Measure = (text: string, font: string) => { width: number; ascent: number; descent: number };

export type SceneOp =
  | { kind: 'path'; d: string; fill: string; opacity: number; blur?: number }
  | { kind: 'circle'; x: number; y: number; r: number; fill: string; opacity: number }
  | { kind: 'rect'; x: number; y: number; width: number; height: number; radius: number; fill: string; opacity: number }
  | { kind: 'dots'; points: MapPoint[]; color: string; width: number; dash: [number, number] }
  | { kind: 'text'; x: number; y: number; text: string; font: string; fill: string; opacity: number; align: 'left' | 'center'; baseline: 'alphabetic' | 'hanging'; halo?: { color: string; width: number } };

/** A layer of the drawing; the charcoal's is rubbed through a grain. */
export interface SceneLayer { grain: boolean; ops: SceneOp[] }

export interface Scene {
  /** The part of the paper drawn: everything on the map and a margin round it. */
  frame: MapFrame;
  layers: SceneLayer[];
}

// MARK: Notes, as the canvas's CSS lays them out (maps.css ▸ Notes)

interface NoteBox {
  width: number;
  height: number;
  /** Lines of words, each with its font, colour and where its line box starts (from the note's top). */
  lines: { text: string; font: string; fill: string; top: number; height: number; left: number }[];
  /** A chip's dot and wash. */
  chip?: { colour: string; dot: { x: number; y: number } };
  opacity: number;
}

/** A note's font size and line height for its size and face (`.map-note.size-*`, `.hand`). */
function noteFont(note: Pick<MapNote, 'size' | 'face'>): { size: number; lineHeight: number; family: string } {
  const hand = note.face === 'hand';
  const size = note.size === 'small' ? (hand ? 17 : 14) : note.size === 'large' ? (hand ? 27 : 24) : hand ? 20 : 17;
  return { size, lineHeight: note.size === 'large' ? 1.2 : 1.3, family: hand ? HAND : SERIF };
}

const font = (o: { italic?: boolean; weight: number; size: number; family: string }) => `${o.italic ? 'italic ' : ''}${o.weight} ${o.size}px ${o.family}`;

/** `white-space: pre-wrap; overflow-wrap: anywhere` at `width`: each paragraph broken after spaces where the next word
 *  wouldn't fit, and a word too long for a line broken anywhere. Spaces at a line's end hang. */
export function wrapText(text: string, width: number, measure: (s: string) => number): string[] {
  const out: string[] = [];
  for (const paragraph of text.split(/\r\n|\n|\r/)) {
    const words = paragraph.match(/\S+\s*|\s+/g) ?? [''];
    let line = '';
    const push = () => {
      out.push(line);
      line = '';
    };
    for (const word of words) {
      if (line !== '' && measure((line + word).trimEnd()) > width) push();
      if (line === '' && measure(word.trimEnd()) > width) {
        // Broken anywhere: as many characters as fit, at least one.
        let rest = [...word];
        while (rest.length && measure(rest.join('').trimEnd()) > width) {
          let n = 1;
          while (n < rest.length && measure(rest.slice(0, n + 1).join('')) <= width) n++;
          line = rest.slice(0, n).join('');
          rest = rest.slice(n);
          push();
        }
        line = rest.join('');
        continue;
      }
      line += word;
    }
    out.push(line);
  }
  return out;
}

/** A note's box and lines: a plain note's words; a document's card (its title, "In the Trash"); a cast chip (a dot and
 *  the name on a wash of their colour). Widths follow `.map-note`: up to 240 wide with no width of its own, else that
 *  width, padding inside. */
export function layoutNote(project: Project, note: MapNote, measure: Measure, chapters: ReadonlyMap<string, string>): NoteBox {
  const { size, lineHeight, family } = noteFont(note);
  const lh = size * lineHeight;
  const live = note.link && linkLives(project, note.link) ? note.link.kind : null;
  const card = live === 'fragment' && note.link ? cardInfo(project, note.link.id, chapters) : null;
  const shown = live === 'entity' && note.link ? linkShown(project, note.link, note.text) : null;
  const chipColour = shown && !shown.missing && shown.kind === 'entity' ? entityCSSColor(shown.color) : null;
  const [padX, padY, padLeft] = chipColour ? [10, 1, 7] : card ? [4, 2, 4] : [3, 1, 3];
  const dot = chipColour ? 7 + 6 : 0;
  const weight = card ? 500 : note.bold ? 650 : 400;
  const mainFont = font({ weight, size, family });
  const colour = chipColour ?? inkOf(note.ink);
  const words = chipColour && shown ? shown.title : card ? card.title : note.text || ' ';
  const sides = padX + padLeft + dot;
  const width = (s: string) => measure(s, mainFont).width;
  const most = note.width !== null ? Math.max(0, note.width - sides) : 240 - sides;
  const natural = Math.max(0, ...words.split(/\r\n|\n|\r/).map((p) => width(p.trimEnd())));
  const contentWidth = note.width !== null ? most : Math.min(most, natural);
  const lines: NoteBox['lines'] = wrapText(words, contentWidth, width).map((text, i) => ({
    text, font: mainFont, fill: colour, top: padY + i * lh, height: lh, left: padLeft + dot,
  }));
  let height = padY * 2 + lines.length * lh;
  if (card?.trashed) {
    const small = 12 * lineHeight;
    lines.push({ text: 'In the Trash', font: font({ italic: true, weight: 400, size: 12, family }), fill: PAPER.faint, top: height - padY, height: small, left: padLeft });
    height += small;
  }
  return {
    width: contentWidth + sides,
    height,
    lines,
    ...(chipColour ? { chip: { colour: chipColour, dot: { x: padLeft + 3.5, y: padY + (lines.length * lh) / 2 } } } : {}),
    opacity: card?.trashed ? 0.55 : 1,
  };
}

// MARK: The scene

/** The map drawn whole, with no selection: regions, then the charcoal (lines, outlines, stacks' lines, ink), then cards'
 *  edges, lines' labels and the notes. */
export function exportScene(o: { content: MapContent; project: Project; measure: Measure; style?: SketchStyle }): Scene {
  const { content, project, measure } = o;
  const style = o.style ?? MapSketch.defaultStyle;
  const chapters = chapterLabels(project);
  const live = (n: MapNote) => (n.link && linkLives(project, n.link) ? n.link.kind : null);
  const outlineOf = (n: MapNote): MapOutline => (live(n) === 'fragment' ? 'box' : live(n) === 'entity' ? 'none' : n.outline);
  const asDrawn: MapContent = { ...content, notes: content.notes.map((n) => (n.link ? { ...n, outline: outlineOf(n) } : n)) };
  const boxes = new Map(content.notes.map((n) => [n.id, layoutNote(project, n, measure, chapters)]));
  const sizes = new Map([...boxes].map(([id, b]) => [id, { width: b.width, height: b.height }]));
  const placed = shownPlaces(asDrawn, sizes);
  const drawn: MapContent = { ...asDrawn, notes: placed.notes };
  const frames = placed.frames;

  // Everything drawn widens the frame, so an oval's or a cloud's bulge isn't cut.
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const reach = (x: number, y: number, pad = 0) => {
    x0 = Math.min(x0, x - pad); y0 = Math.min(y0, y - pad); x1 = Math.max(x1, x + pad); y1 = Math.max(y1, y + pad);
  };
  const markOps = (marks: SketchMark[], fill: string): SceneOp[] => marks.map((m) => {
    for (const p of m.points) reach(p.x, p.y, (('width' in p ? p.width : 0) || 0) / 2);
    return { kind: 'path', d: markPath(m), fill, opacity: m.opacity };
  });

  const regions: SceneOp[] = [];
  for (const r of drawn.regions) {
    const wash = MapSketch.region(r.at.x, r.at.y, r.width, r.height, r.id, style);
    for (const p of wash.points) reach(p.x, p.y, 14);
    regions.push({ kind: 'path', d: markPath(wash), fill: inkOf(r.ink), opacity: 0.18, blur: 7 });
    const label = r.label.trim();
    if (label) regions.push({ kind: 'text', x: r.at.x + 16, y: r.at.y + 14, text: label, font: font({ italic: true, weight: 400, size: 15, family: SERIF }), fill: inkOf(r.ink), opacity: 1, align: 'left', baseline: 'hanging' });
  }

  const ink: SceneOp[] = [];
  const labels: SceneOp[] = [];
  const stacks = MapArrange.stacks(drawn);
  for (const line of drawn.lines) {
    if (MapArrange.isWithinStack(line, stacks)) continue;
    const ends = MapLayout.lineEnds(line, drawn, frames);
    if (!ends) continue;
    const [a, b] = ends;
    const passes = MapSketch.line(a, b, line.id, style);
    if (!passes.length) continue;
    const spine = passes[0].points;
    const colour = inkOf(line.ink);
    if (line.style === 'dotted') {
      for (const p of spine) reach(p.x, p.y, 1);
      ink.push({ kind: 'dots', points: spine.map((p) => ({ x: p.x, y: p.y })), color: colour, width: 1.5, dash: [1.5, 5] });
    } else {
      ink.push(...markOps(passes, colour));
    }
    if (line.arrow !== 'none') ink.push(...markOps(MapSketch.arrowhead(b, spine[Math.max(0, spine.length - 3)], line.id, style), colour));
    if (line.arrow === 'both') ink.push(...markOps(MapSketch.arrowhead(a, spine[Math.min(spine.length - 1, 2)], `${line.id}|start`, style), colour));
    const mid = midPoint(spine);
    const label = line.label.trim();
    if (label && mid) {
      const f = font({ italic: true, weight: 400, size: 14, family: SERIF });
      const w = measure(label, f).width;
      reach(mid.x - w / 2, mid.y - 12, 4);
      reach(mid.x + w / 2, mid.y + 8, 4);
      labels.push({ kind: 'text', x: mid.x, y: mid.y + 5, text: label, font: f, fill: colour, opacity: 1, align: 'center', baseline: 'alphabetic', halo: { color: PAPER.paper, width: 7 } });
    }
  }
  for (const [head, members] of stacks) {
    const top = frames.get(head), last = frames.get(members[members.length - 1]);
    if (!top || !last) continue;
    const [a, b] = stackLine(top, last);
    ink.push(...markOps(MapSketch.line(a, b, `${head}|stack`, style), PAPER.ink));
  }
  const edges: SceneOp[] = [];
  const notes: SceneOp[] = [];
  for (const n of drawn.notes) {
    const f = frames.get(n.id)!;
    const box = boxes.get(n.id)!;
    reach(f.x, f.y);
    reach(f.x + f.width, f.y + f.height);
    const shape = outlineOf(n);
    if (shape !== 'none') ink.push(...markOps(MapSketch.outline(shape, f.x, f.y, f.width, f.height, n.id, style), inkOf(n.ink)));
    const card = live(n) === 'fragment' && n.link ? cardInfo(project, n.link.id, chapters) : null;
    if (card?.edge) edges.push({ kind: 'rect', x: f.x - 6, y: f.y - 4, width: f.width + 12, height: 3, radius: 1, fill: card.edge, opacity: card.trashed ? 0.45 : 1 });
    if (box.chip) {
      notes.push({ kind: 'rect', x: f.x, y: f.y, width: f.width, height: f.height, radius: f.height / 2, fill: box.chip.colour, opacity: 0.14 });
      notes.push({ kind: 'circle', x: f.x + box.chip.dot.x, y: f.y + box.chip.dot.y, r: 3.5, fill: box.chip.colour, opacity: 1 });
    }
    for (const line of box.lines) {
      if (line.text === '') continue;
      const m = measure(line.text, line.font);
      // CSS puts the text's ascent and descent in the middle of the line box.
      const baseline = f.y + line.top + (line.height - (m.ascent + m.descent)) / 2 + m.ascent;
      notes.push({ kind: 'text', x: f.x + line.left, y: baseline, text: line.text, font: line.font, fill: line.fill, opacity: box.opacity, align: 'left', baseline: 'alphabetic' });
    }
  }
  for (const s of drawn.strokes) {
    const points = strokeMarkPoints(placedStroke(s), s.width);
    const colour = inkOf(s.ink);
    if (points.length === 1) {
      reach(points[0].x, points[0].y, points[0].width);
      ink.push({ kind: 'circle', x: points[0].x, y: points[0].y, r: points[0].width / 2, fill: colour, opacity: 0.9 });
    } else if (points.length > 1) {
      for (const p of points) reach(p.x, p.y, p.width);
      ink.push({ kind: 'path', d: markPath({ kind: 'stroke', opacity: 0.9, points }), fill: colour, opacity: 0.9 });
    }
  }
  if (!Number.isFinite(x0)) {
    x0 = 0; y0 = 0; x1 = 200; y1 = 120;
  }
  const frame = { x: x0 - EXPORT_MARGIN, y: y0 - EXPORT_MARGIN, width: x1 - x0 + EXPORT_MARGIN * 2, height: y1 - y0 + EXPORT_MARGIN * 2 };
  return { frame, layers: [{ grain: false, ops: regions }, { grain: true, ops: ink }, { grain: false, ops: [...edges, ...labels, ...notes] }] };
}

// MARK: Painting (in the browser)

/** The largest picture made: browsers refuse canvases much past this. */
const MOST_PIXELS = 120_000_000;
const MOST_SIDE = 16_000;

/** The scale an export is drawn at: 2×, less for a map too large for a canvas. */
export function exportScale(frame: MapFrame, wanted = 2): number {
  const side = MOST_SIDE / Math.max(frame.width, frame.height);
  const area = Math.sqrt(MOST_PIXELS / Math.max(1, frame.width * frame.height));
  return Math.max(0.1, Math.min(wanted, side, area));
}

/** A small deterministic noise (mulberry32), so every export of a map is the same picture. */
function noise(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A tile of grain: `alpha(n)` of the colour at each pixel, n being noise in 0…1. */
function grainTile(colour: [number, number, number], alpha: (n: number) => number, seed: number): HTMLCanvasElement {
  const size = 256;
  const tile = document.createElement('canvas');
  tile.width = tile.height = size;
  const g = tile.getContext('2d')!;
  const image = g.createImageData(size, size);
  const random = noise(seed);
  for (let i = 0; i < size * size; i++) {
    const n = (random() + random()) / 2;
    image.data[i * 4] = colour[0];
    image.data[i * 4 + 1] = colour[1];
    image.data[i * 4 + 2] = colour[2];
    image.data[i * 4 + 3] = Math.round(Math.min(1, Math.max(0, alpha(n))) * 255);
  }
  g.putImageData(image, 0, 0);
  return tile;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

function paintOp(ctx: CanvasRenderingContext2D, op: SceneOp, scale: number) {
  ctx.save();
  ctx.globalAlpha = 'opacity' in op ? op.opacity : 1;
  switch (op.kind) {
    case 'path': {
      ctx.fillStyle = op.fill;
      if (op.blur) {
        if ('filter' in ctx) (ctx as CanvasRenderingContext2D).filter = `blur(${op.blur * scale}px)`;
        else (ctx as CanvasRenderingContext2D).globalAlpha = op.opacity * 0.8;
      }
      ctx.fill(new Path2D(op.d));
      break;
    }
    case 'circle':
      ctx.fillStyle = op.fill;
      ctx.beginPath();
      ctx.arc(op.x, op.y, op.r, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'rect':
      ctx.fillStyle = op.fill;
      ctx.beginPath();
      ctx.roundRect(op.x, op.y, op.width, op.height, op.radius);
      ctx.fill();
      break;
    case 'dots':
      ctx.globalAlpha = 1;
      ctx.strokeStyle = op.color;
      ctx.lineWidth = op.width;
      ctx.lineCap = 'round';
      ctx.setLineDash(op.dash);
      ctx.beginPath();
      op.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
      break;
    case 'text':
      ctx.font = op.font;
      ctx.textAlign = op.align;
      ctx.textBaseline = op.baseline;
      if (op.halo) {
        ctx.strokeStyle = op.halo.color;
        ctx.lineWidth = op.halo.width;
        ctx.lineJoin = 'round';
        ctx.strokeText(op.text, op.x, op.y);
      }
      ctx.fillStyle = op.fill;
      ctx.fillText(op.text, op.x, op.y);
      break;
  }
  ctx.restore();
}

/** Paints a scene at `scale` pixels to the point: the paper and its grain, then each layer, the charcoal's rubbed. */
export function paintScene(scene: Scene, scale: number): HTMLCanvasElement {
  const { frame } = scene;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(frame.width * scale));
  canvas.height = Math.max(1, Math.round(frame.height * scale));
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = PAPER.paper;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  // The paper's grain (maps.css `.map-paper-grain`: the ink at 6% through the noise).
  ctx.fillStyle = ctx.createPattern(grainTile([0x2f, 0x2a, 0x26], (n) => clamp01(1.6 * n - 0.7) * 0.06, 11), 'repeat')!;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const place = (c: CanvasRenderingContext2D) => c.setTransform(scale, 0, 0, scale, -frame.x * scale, -frame.y * scale);
  for (const layer of scene.layers) {
    if (!layer.grain) {
      place(ctx);
      for (const op of layer.ops) paintOp(ctx, op, scale);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      continue;
    }
    // Charcoal: drawn on its own sheet, rubbed away where the grain says (`#map-grain`: kept as 1.9 − 2.2n), then laid on.
    const sheet = document.createElement('canvas');
    sheet.width = canvas.width;
    sheet.height = canvas.height;
    const s = sheet.getContext('2d')!;
    place(s);
    for (const op of layer.ops) paintOp(s, op, scale);
    s.setTransform(1, 0, 0, 1, 0, 0);
    s.globalCompositeOperation = 'destination-out';
    s.fillStyle = s.createPattern(grainTile([0, 0, 0], (n) => 1 - clamp01(1.9 - 2.2 * n), 3), 'repeat')!;
    s.fillRect(0, 0, sheet.width, sheet.height);
    ctx.drawImage(sheet, 0, 0);
  }
  return canvas;
}

/** Waits for the faces an export draws in, so the canvas measures and paints with them, not a fallback. */
export async function loadExportFonts(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return;
  const wanted = ['400 17px Newsreader', '650 17px Newsreader', 'italic 400 17px Newsreader', '400 20px Caveat', '650 20px Caveat'];
  await Promise.all(wanted.map((f) => document.fonts.load(f).catch(() => [])));
  await document.fonts.ready;
}

/** Measuring with a 2D canvas (in the page's fonts). */
export function canvasMeasure(): Measure {
  const ctx = document.createElement('canvas').getContext('2d')!;
  const cache = new Map<string, { width: number; ascent: number; descent: number }>();
  return (text, f) => {
    const key = f + '\u0000' + text;
    const hit = cache.get(key);
    if (hit) return hit;
    ctx.font = f;
    const m = ctx.measureText(text);
    const out = { width: m.width, ascent: m.fontBoundingBoxAscent ?? m.actualBoundingBoxAscent, descent: m.fontBoundingBoxDescent ?? m.actualBoundingBoxDescent };
    cache.set(key, out);
    return out;
  };
}

function blobOf(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('The picture couldn’t be made.'))), type, quality));
}

/** The map as a PNG at 2×. */
export async function mapPNG(o: { content: MapContent; project: Project; style?: SketchStyle }): Promise<Uint8Array> {
  await loadExportFonts();
  const scene = exportScene({ ...o, measure: canvasMeasure() });
  const canvas = paintScene(scene, exportScale(scene.frame));
  return new Uint8Array(await (await blobOf(canvas, 'image/png')).arrayBuffer());
}

/** The map as a one-page PDF the map's size (a point to a point of the map), holding the picture at 2× as a JPEG. */
export async function mapPDF(o: { content: MapContent; project: Project; style?: SketchStyle; title: string }): Promise<Uint8Array> {
  await loadExportFonts();
  const scene = exportScene({ ...o, measure: canvasMeasure() });
  const canvas = paintScene(scene, exportScale(scene.frame));
  const jpeg = new Uint8Array(await (await blobOf(canvas, 'image/jpeg', 0.92)).arrayBuffer());
  return jpegPDF(jpeg, { width: canvas.width, height: canvas.height }, { width: scene.frame.width, height: scene.frame.height }, o.title);
}

// MARK: The PDF

/** The largest page a PDF reader takes (200 inches); a larger map is scaled down onto it. */
const MOST_PAGE = 14_400;

/** A text string for a PDF's Info: literal when plain ASCII, else UTF-16BE with its byte-order mark. */
function pdfString(text: string): string {
  if (/^[\x20-\x7e]*$/.test(text)) return `(${text.replace(/[\\()]/g, (c) => `\\${c}`)})`;
  let hex = 'FEFF';
  for (let i = 0; i < text.length; i++) hex += text.charCodeAt(i).toString(16).toUpperCase().padStart(4, '0');
  return `<${hex}>`;
}

const pdfNumber = (v: number) => (Math.round(v * 100) / 100).toString();

/** A one-page PDF showing a JPEG (`pixels` its size) over a whole page of `page` points. */
export function jpegPDF(jpeg: Uint8Array, pixels: { width: number; height: number }, page: { width: number; height: number }, title: string): Uint8Array {
  const shrink = Math.min(1, MOST_PAGE / Math.max(page.width, page.height));
  const w = pdfNumber(page.width * shrink), h = pdfNumber(page.height * shrink);
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const add = (bytes: Uint8Array | string) => {
    const b = typeof bytes === 'string' ? encoder.encode(bytes) : bytes;
    parts.push(b);
    length += b.length;
  };
  const object = (n: number, body: string) => {
    offsets[n] = length;
    add(`${n} 0 obj\n${body}\nendobj\n`);
  };
  add('%PDF-1.4\n');
  // The binary marker: a comment of four bytes over 127, so transfer programs treat the file as binary.
  add(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));
  object(1, '<< /Type /Catalog /Pages 2 0 R >>');
  object(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  object(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] /Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>`);
  const content = `q ${w} 0 0 ${h} 0 0 cm /Im0 Do Q`;
  object(4, `<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
  offsets[5] = length;
  add(`5 0 obj\n<< /Type /XObject /Subtype /Image /Width ${pixels.width} /Height ${pixels.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
  add(jpeg);
  add('\nendstream\nendobj\n');
  object(6, `<< /Title ${pdfString(title)} /Producer (Five Rooms) >>`);
  const xref = length;
  add(`xref\n0 7\n0000000000 65535 f \n${offsets.slice(1).map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('')}`);
  add(`trailer\n<< /Size 7 /Root 1 0 R /Info 6 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  const out = new Uint8Array(length);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

/** A file name from a map's title: the characters file systems refuse replaced. */
export const exportFileName = (title: string, extension: 'png' | 'pdf') => `${(title.trim() || 'Mind Map').replace(/[\\/:*?"<>|\u0000-\u001f]/g, '-')}.${extension}`;
